import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import {
  TEXT_LINE_HEIGHT,
  annotationBounds,
  clampScale,
  drawAnnotation,
  findAnnotationAt,
  movedFields,
  resizeHandle,
  resizedFields,
} from "../lib/annotations";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const DRAG_TOOLS = new Set([
  "ink",
  "highlight",
  "rect",
  "oval",
  "arrow",
  "line",
]);
const PLACE_TOOLS = new Set([
  "tick",
  "cross",
  "mark",
  "qmark",
  "comment",
  "stamp",
]);
const PAGE_PADDING = 48;

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const isTyping = (target) =>
  target?.tagName === "INPUT" ||
  target?.tagName === "TEXTAREA" ||
  target?.isContentEditable;

function cursorFor(tool, spaceDown, dragging) {
  if (tool === "pan" || spaceDown) return dragging ? "grabbing" : "grab";
  if (tool === "select") return "default";
  if (tool === "eraser") return "cell";
  if (tool === "text") return "text";
  return "crosshair";
}

export default function PageViewer({
  pdfDoc,
  pageNo,
  view,
  onViewChange,
  annotations = [],
  tool = "select",
  options = {},
  readOnly = false,
  selectedId = null,
  onSelect,
  onAdd,
  onUpdate,
  onRemove,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const editorInputRef = useRef(null);
  const editorRef = useRef(null);
  const draftRef = useRef(null);
  const moveRef = useRef(null);
  const resizeRef = useRef(null);
  const panRef = useRef(null);
  const erasingRef = useRef(false);
  const activePointerRef = useRef(null);
  const penSeenRef = useRef(false);
  const paintRef = useRef(() => {});
  const frameRef = useRef(null);

  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [spaceDown, setSpaceDown] = useState(false);
  const [dragging, setDragging] = useState(false);

  const pageAnnotations = useMemo(
    () =>
      pageInfo ? annotations.filter((ann) => ann.page === pageInfo.no) : [],
    [annotations, pageInfo],
  );

  const cssWidth = pageInfo ? pageInfo.width * view.scale : 0;
  const cssHeight = pageInfo ? pageInfo.height * view.scale : 0;

  // Render offscreen first, so the old page stays visible and nothing flashes.
  useEffect(() => {
    if (!pdfDoc) return undefined;
    let cancelled = false;
    let task = null;

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const page = await pdfDoc.getPage(pageNo);
        if (cancelled) return;

        const base = page.getViewport({ scale: 1 });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: view.scale * dpr });
        const offscreen = document.createElement("canvas");
        offscreen.width = Math.ceil(viewport.width);
        offscreen.height = Math.ceil(viewport.height);

        task = page.render({
          canvasContext: offscreen.getContext("2d"),
          viewport,
        });
        await task.promise;
        if (cancelled || !canvasRef.current) return;

        const canvas = canvasRef.current;
        canvas.width = offscreen.width;
        canvas.height = offscreen.height;
        canvas.getContext("2d").drawImage(offscreen, 0, 0);
        setPageInfo({ no: pageNo, width: base.width, height: base.height });
        setLoading(false);
      } catch (error) {
        if (error?.name !== "RenderingCancelledException") {
          console.error(error);
          setLoading(false);
        }
      }
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      task?.cancel();
    };
  }, [pdfDoc, pageNo, view.scale]);

  // Keep fit-to-width and fit-to-page correct when panels open or the window resizes.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pageInfo || !view.fit) return undefined;

    const fit = () => {
      const width = container.clientWidth - PAGE_PADDING;
      const height = container.clientHeight - PAGE_PADDING - 36;
      const byWidth = width / pageInfo.width;
      const next = clampScale(
        view.fit === "width"
          ? Math.min(byWidth, 2)
          : Math.min(byWidth, height / pageInfo.height),
      );
      if (Math.abs(next - view.scale) > 0.005)
        onViewChange({ scale: next, fit: view.fit });
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [pageInfo, view.fit, view.scale, onViewChange]);

  // Ctrl + wheel zoom needs a non-passive listener.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const delta = event.deltaY < 0 ? 0.1 : -0.1;
      onViewChange((current) => ({
        scale: clampScale(current.scale + delta),
        fit: null,
      }));
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [onViewChange]);

  // Hold Space to pan with any tool.
  useEffect(() => {
    const down = (event) => {
      if (event.code === "Space" && !isTyping(event.target)) {
        event.preventDefault();
        setSpaceDown(true);
      }
    };
    const up = (event) => {
      if (event.code === "Space") setSpaceDown(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  paintRef.current = () => {
    const canvas = overlayRef.current;
    if (!canvas || !pageInfo) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.round(cssWidth * dpr);
    const pixelHeight = Math.round(cssHeight * dpr);
    if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
    if (canvas.height !== pixelHeight) canvas.height = pixelHeight;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    pageAnnotations.forEach((ann) => {
      if (editor?.id === ann.id) return;

      const moving = moveRef.current?.id === ann.id;
      const resizing = resizeRef.current?.id === ann.id;
      let shown = ann;
      if (moving) {
        shown = {
          ...ann,
          ...movedFields(ann, moveRef.current.dx, moveRef.current.dy),
        };
      } else if (resizing) {
        shown = { ...ann, ...resizeRef.current.changes };
      }

      drawAnnotation(ctx, shown, cssWidth, cssHeight, view.scale);

      if (ann.id === selectedId) {
        const box = annotationBounds(shown, cssWidth, cssHeight, view.scale);
        ctx.save();
        ctx.strokeStyle = "#0a6cf0";
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 4]);
        ctx.strokeRect(box.x - 5, box.y - 5, box.w + 10, box.h + 10);

        const handle = resizeHandle(shown, cssWidth, cssHeight, view.scale);
        if (handle) {
          ctx.setLineDash([]);
          ctx.fillStyle = "#fff";
          ctx.fillRect(handle.x - 5, handle.y - 5, 10, 10);
          ctx.strokeRect(handle.x - 5, handle.y - 5, 10, 10);
        }
        ctx.restore();
      }
    });

    if (draftRef.current)
      drawAnnotation(ctx, draftRef.current, cssWidth, cssHeight, view.scale);
  };

  useEffect(() => {
    paintRef.current();
  });

  const requestPaint = () => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      paintRef.current();
    });
  };

  const pointerPosition = (event) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return {
      x: clamp01((event.clientX - rect.left) / rect.width),
      y: clamp01((event.clientY - rect.top) / rect.height),
    };
  };

  const annotationAt = (position) =>
    findAnnotationAt(
      pageAnnotations,
      position.x * cssWidth,
      position.y * cssHeight,
      cssWidth,
      cssHeight,
      view.scale,
    );

  const updateEditor = (patch) => {
    editorRef.current = { ...editorRef.current, ...patch };
    setEditor(editorRef.current);
  };

  const openEditor = (nextEditor) => {
    editorRef.current = nextEditor;
    setEditor(nextEditor);
    requestAnimationFrame(() => editorInputRef.current?.focus());
  };

  const cancelEditor = () => {
    editorRef.current = null;
    setEditor(null);
  };

  const commitEditor = () => {
    const current = editorRef.current;
    if (!current) return;
    cancelEditor();

    const text = current.text.trim();
    if (current.id) {
      if (!text) onRemove?.(current.id);
      else if (text !== current.originalText) onUpdate?.(current.id, { text });
      return;
    }

    if (text) {
      onAdd?.({
        type: "text",
        x: current.x,
        y: current.y,
        text,
        size: current.size,
        color: current.color,
        bold: current.bold,
        italic: current.italic,
        underline: current.underline,
      });
    }
  };

  const placeAnnotation = (position) => {
    if (tool === "comment" && options.commentText) {
      onAdd?.({
        type: "text",
        ...position,
        text: options.commentText,
        size: options.fontSize,
        color: options.textColor,
        bold: options.bold,
        italic: options.italic,
        underline: options.underline,
      });
    } else if (tool === "stamp" && options.stampText) {
      onAdd?.({ type: "stamp", ...position, text: options.stampText });
    } else if (tool === "mark") {
      onAdd?.({
        type: "mark",
        ...position,
        value: options.markValue,
        color: options.color,
      });
    } else if (tool === "tick" || tool === "cross" || tool === "qmark") {
      onAdd?.({ type: tool, ...position });
    }
  };

  const startDraft = (position) => {
    const base = { type: tool, color: options.color, size: options.thickness };

    if (tool === "ink") {
      draftRef.current = {
        ...base,
        x: position.x,
        y: position.y,
        points: [position],
      };
    } else if (tool === "highlight") {
      draftRef.current = {
        type: "highlight",
        x: position.x,
        y: position.y,
        w: 0,
        h: 0,
      };
    } else if (tool === "rect" || tool === "oval") {
      draftRef.current = { ...base, x: position.x, y: position.y, w: 0, h: 0 };
    } else {
      draftRef.current = {
        ...base,
        x: position.x,
        y: position.y,
        x2: position.x,
        y2: position.y,
      };
    }

    draftRef.current.startX = position.x;
    draftRef.current.startY = position.y;
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === "pen") penSeenRef.current = true;
    if (event.pointerType === "touch" && penSeenRef.current) return;
    if (activePointerRef.current !== null) return;
    if (event.button !== 0) return;

    activePointerRef.current = event.pointerId;
    event.currentTarget.setPointerCapture?.(event.pointerId);

    if (tool === "pan" || spaceDown || readOnly) {
      const container = containerRef.current;
      panRef.current = {
        x: event.clientX,
        y: event.clientY,
        left: container.scrollLeft,
        top: container.scrollTop,
      };
      setDragging(true);
      return;
    }

    if (editorRef.current) {
      commitEditor();
      return;
    }

    const position = pointerPosition(event);

    if (tool === "select") {
      const selected = pageAnnotations.find((ann) => ann.id === selectedId);
      const handle =
        selected && resizeHandle(selected, cssWidth, cssHeight, view.scale);
      if (
        handle &&
        Math.hypot(
          position.x * cssWidth - handle.x,
          position.y * cssHeight - handle.y,
        ) <= 10
      ) {
        resizeRef.current = { id: selected.id, ann: selected, changes: {} };
        return;
      }

      const hit = annotationAt(position);
      onSelect?.(hit?.id || null);
      if (hit)
        moveRef.current = {
          id: hit.id,
          ann: hit,
          start: position,
          dx: 0,
          dy: 0,
        };
      requestPaint();
      return;
    }

    if (tool === "eraser") {
      erasingRef.current = true;
      const hit = annotationAt(position);
      if (hit) onRemove?.(hit.id);
      return;
    }

    if (tool === "text") {
      openEditor({
        id: null,
        x: position.x,
        y: position.y,
        text: "",
        size: options.fontSize,
        color: options.textColor,
        bold: options.bold,
        italic: options.italic,
        underline: options.underline,
      });
      return;
    }

    if (PLACE_TOOLS.has(tool)) {
      placeAnnotation(position);
      return;
    }

    if (DRAG_TOOLS.has(tool)) {
      startDraft(position);
      requestPaint();
    }
  };

  const handlePointerMove = (event) => {
    if (event.pointerId !== activePointerRef.current) return;

    if (panRef.current) {
      const container = containerRef.current;
      container.scrollLeft =
        panRef.current.left - (event.clientX - panRef.current.x);
      container.scrollTop =
        panRef.current.top - (event.clientY - panRef.current.y);
      return;
    }

    if (erasingRef.current) {
      const hit = annotationAt(pointerPosition(event));
      if (hit) onRemove?.(hit.id);
      return;
    }

    if (resizeRef.current) {
      resizeRef.current.changes = resizedFields(
        resizeRef.current.ann,
        pointerPosition(event),
        cssWidth,
        cssHeight,
        view.scale,
      );
      requestPaint();
      return;
    }

    if (moveRef.current) {
      const position = pointerPosition(event);
      moveRef.current.dx = position.x - moveRef.current.start.x;
      moveRef.current.dy = position.y - moveRef.current.start.y;
      requestPaint();
      return;
    }

    const draft = draftRef.current;
    if (!draft) return;

    const position = pointerPosition(event);
    if (draft.type === "ink") {
      const samples = event.nativeEvent.getCoalescedEvents?.() || [event];
      samples.forEach((sample) => draft.points.push(pointerPosition(sample)));
    } else if (draft.type === "line" || draft.type === "arrow") {
      draft.x2 = position.x;
      draft.y2 = position.y;
    } else {
      draft.x = Math.min(draft.startX, position.x);
      draft.y = Math.min(draft.startY, position.y);
      draft.w = Math.abs(position.x - draft.startX);
      draft.h = Math.abs(position.y - draft.startY);
    }
    requestPaint();
  };

  const finishDraft = () => {
    const draft = draftRef.current;
    draftRef.current = null;
    if (!draft) return;

    const { startX, startY, ...annotation } = draft;
    const tiny = 0.006;

    if (annotation.type === "ink") {
      onAdd?.({
        ...annotation,
        points: annotation.points.map((point) => ({
          x: Number(point.x.toFixed(4)),
          y: Number(point.y.toFixed(4)),
        })),
      });
    } else if (annotation.type === "line" || annotation.type === "arrow") {
      if (Math.hypot(annotation.x2 - startX, annotation.y2 - startY) > tiny)
        onAdd?.(annotation);
    } else if (annotation.w > tiny || annotation.h > tiny) {
      onAdd?.(annotation);
    } else if (annotation.type === "highlight") {
      onAdd?.({ ...annotation, x: startX, y: startY - 0.01, w: 0.15, h: 0.03 });
    }

    requestPaint();
  };

  const handlePointerUp = (event) => {
    if (event.pointerId !== activePointerRef.current) return;

    activePointerRef.current = null;
    panRef.current = null;
    erasingRef.current = false;
    setDragging(false);

    if (resizeRef.current) {
      const { id, changes } = resizeRef.current;
      resizeRef.current = null;
      if (Object.keys(changes).length) onUpdate?.(id, changes);
      requestPaint();
      return;
    }

    if (moveRef.current) {
      const { id, ann, dx, dy } = moveRef.current;
      moveRef.current = null;
      if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001)
        onUpdate?.(id, movedFields(ann, dx, dy));
      requestPaint();
      return;
    }

    finishDraft();
  };

  const handleDoubleClick = (event) => {
    if (readOnly || tool !== "select") return;
    const hit = annotationAt(pointerPosition(event));
    if (hit?.type !== "text") return;

    openEditor({
      id: hit.id,
      x: hit.x,
      y: hit.y,
      text: hit.text,
      originalText: hit.text,
      size: (hit.size || 14) * (hit.scale || 1),
      color: hit.color || "#e53935",
      bold: hit.bold,
      italic: hit.italic,
      underline: hit.underline,
    });
  };

  if (!pdfDoc) {
    return (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="text.secondary">Loading PDF…</Typography>
      </Box>
    );
  }

  const editorFontSize = editor ? editor.size * view.scale : 0;

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        overflow: "auto",
        scrollbarGutter: "stable",
        display: "flex",
        justifyContent: "safe center",
        alignItems: "flex-start",
        px: 3,
        pt: 3,
        pb: 9,
        bgcolor: "#e9eef6",
      }}
    >
      {loading && (
        <CircularProgress
          size={26}
          sx={{ position: "absolute", top: 16, right: 24, zIndex: 3 }}
        />
      )}

      <Paper
        elevation={3}
        sx={{
          position: "relative",
          flexShrink: 0,
          width: cssWidth || "auto",
          height: cssHeight || "auto",
          borderRadius: 0,
          bgcolor: "#fff",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: "block",
            width: cssWidth || 0,
            height: cssHeight || 0,
          }}
        />
        <canvas
          ref={overlayRef}
          onMouseDown={(event) => event.preventDefault()}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          style={{
            position: "absolute",
            inset: 0,
            width: cssWidth || 0,
            height: cssHeight || 0,
            cursor: readOnly ? "grab" : cursorFor(tool, spaceDown, dragging),
            touchAction: tool === "pan" || readOnly ? "auto" : "none",
          }}
        />

        {editor && (
          <textarea
            ref={editorInputRef}
            value={editor.text}
            rows={Math.max(1, editor.text.split("\n").length)}
            placeholder="Type comment"
            aria-label="Comment text"
            onChange={(event) => updateEditor({ text: event.target.value })}
            onBlur={commitEditor}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                commitEditor();
              }
              if (event.key === "Escape") cancelEditor();
            }}
            style={{
              position: "absolute",
              zIndex: 5,
              left: editor.x * cssWidth,
              top: editor.y * cssHeight - editorFontSize,
              width: Math.max(180, cssWidth * 0.4),
              maxWidth: cssWidth - editor.x * cssWidth,
              padding: 0,
              resize: "none",
              overflow: "hidden",
              background: "rgba(255,255,255,.92)",
              border: "1px dashed #0a6cf0",
              outline: "none",
              fontFamily: "sans-serif",
              fontSize: editorFontSize,
              lineHeight: TEXT_LINE_HEIGHT,
              fontWeight: editor.bold ? 700 : 400,
              fontStyle: editor.italic ? "italic" : "normal",
              textDecoration: editor.underline ? "underline" : "none",
              color: editor.color,
            }}
          />
        )}
      </Paper>
    </Box>
  );
}
